/**
 * Reference designator assignment.
 *
 * The stock libraries ship reference templates with a `?` standing in for the
 * package number — `R?`, `C?`, `U?f`. Annotation replaces the `?` with a
 * number, which is what turns a drawing into a netlist: without it every 7404
 * gate on the sheet claims the same designator.
 *
 * Multi-gate parts complicate this. A 7404 is six inverters in one package, so
 * six placed gates should become `U1a`…`U1f` — one physical part — rather than
 * six packages. {@link generateSymbol} has already stamped each gate's letter,
 * so annotation only has to decide which package each gate belongs to.
 *
 * Ordering follows the original's Resequence Components dialog (RESEQCOMP in
 * OG.h:1437): a positional scan, defaulting to horizontal rows from the upper
 * left, so numbering follows how the sheet reads rather than placement order.
 */

import type { CadDocument, ComponentEntity } from '../model.ts';
import { gateCount, refPrefix } from '../circad/srclib.ts';
import { KEY_SEP } from '../keys.ts';

export interface AnnotateOptions {
  /** Scan direction. Horizontal numbers across rows, vertical down columns. */
  pattern?: 'horizontal' | 'vertical';
  /** Corner to start from. */
  from?: 'upper-left' | 'lower-left' | 'upper-right' | 'lower-right';
  /** First package number. */
  start?: number;
  /** Increment between packages. */
  step?: number;
  /**
   * Re-number everything, including designators already assigned. Off by
   * default so annotating twice does not renumber a board you have laid out.
   */
  renumberAll?: boolean;
  /**
   * Row banding tolerance for the positional sort, in inches. Components
   * within this distance vertically are treated as the same row.
   */
  rowTolerance?: number;
}

export interface AnnotateResult {
  /** Components whose designator changed. */
  changed: { id: number; from: string; to: string }[];
  /** Distinct packages after annotation. */
  packages: number;
  /** Components with no `P1` template to annotate. */
  skipped: number;
}

/** True when a designator still needs a number. */
export function needsAnnotation(ref: string): boolean {
  return ref.includes('?');
}

/**
 * Assign reference designators in place.
 *
 * Returns what changed so the caller can report it; the original's dialog
 * likewise previews rather than silently renumbering.
 */
export function annotate(
  source: CadDocument | readonly CadDocument[],
  opts: AnnotateOptions = {},
): AnnotateResult {
  const pattern = opts.pattern ?? 'horizontal';
  const from = opts.from ?? 'upper-left';
  const start = opts.start ?? 1;
  const step = opts.step ?? 1;
  const tolerance = opts.rowTolerance ?? 0.05;

  // A design's sheets are numbered as one run. Each sheet is drawn in its own
  // frame around the same origin, so a positional sort across the set would
  // interleave them and `U1` would be whichever part happened to sit highest on
  // any sheet — and, worse, annotating sheet by sheet gives every sheet its own
  // `U1` for a different chip. Sheet order first, then position within it.
  const sheets: readonly CadDocument[] = Array.isArray(source) ? source : [source as CadDocument];
  const comps = sheets.flatMap(
    (d) => d.entities.filter((e): e is ComponentEntity => e.kind === 'component'));

  const changed: AnnotateResult['changed'] = [];
  let skipped = 0;

  // --- Positional order, sheet by sheet ------------------------------------
  const ordered = sheets.flatMap((d) => d.entities
    .filter((e): e is ComponentEntity => e.kind === 'component')
    .sort((a, b) => comparePosition(a, b, pattern, from, tolerance)));

  // --- Group by part type --------------------------------------------------
  // Gates can only share a package if they are the same part with the same
  // footprint; a 7400 gate and a 7404 gate are never one chip.
  const groups = new Map<string, ComponentEntity[]>();
  for (const c of ordered) {
    const template = c.params[1];
    if (!template) { skipped++; continue; }
    if (!opts.renumberAll && !needsAnnotation(template)) continue;

    const key = `${refPrefix(template)}${KEY_SEP}${c.name}${KEY_SEP}${c.footprint ?? ''}`;
    let g = groups.get(key);
    if (!g) { g = []; groups.set(key, g); }
    g.push(c);
  }

  // --- Numbers already in use, so we never collide with a fixed designator --
  const used = new Map<string, Set<number>>();
  for (const c of comps) {
    const ref = c.params[1];
    if (!ref || needsAnnotation(ref)) continue;
    const m = /^([A-Za-z_]+)(\d+)/.exec(ref);
    if (!m) continue;
    const prefix = m[1]!;
    let s = used.get(prefix);
    if (!s) { s = new Set(); used.set(prefix, s); }
    s.add(parseInt(m[2]!, 10));
  }

  let packageCount = 0;

  for (const group of groups.values()) {
    const first = group[0]!;
    const template = first.params[1]!;
    const prefix = refPrefix(template);
    // The component records the package size, because the template's trailing
    // letter has already been replaced by the placed gate's own letter.
    const gates = first.gates ?? gateCount(template);

    // Which gate letters each package number has already consumed.
    const filled = new Map<number, Set<string>>();
    const taken = used.get(prefix) ?? new Set<number>();
    if (!used.has(prefix)) used.set(prefix, taken);

    for (const c of group) {
      const own = c.params[1]!;
      const letter = /\?([a-z])$/.exec(own)?.[1];

      // Find the lowest package that has room for this gate letter. A part
      // with one gate always takes a fresh number.
      let n = start;
      for (;;) {
        if (taken.has(n) && !filled.has(n)) { n += step; continue; }
        const slots = filled.get(n);
        if (gates <= 1) {
          if (!slots && !taken.has(n)) break;
        } else if (!slots || (letter !== undefined && !slots.has(letter))) {
          break;
        }
        n += step;
      }

      let slots = filled.get(n);
      if (!slots) { slots = new Set(); filled.set(n, slots); packageCount++; }
      if (letter !== undefined) slots.add(letter);
      taken.add(n);

      const next = own.replace('?', String(n));
      if (next !== own) {
        changed.push({ id: c.id, from: own, to: next });
        c.params[1] = next;
      }
    }
  }

  return { changed, packages: packageCount, skipped };
}

/** Sort comparator implementing the RESEQCOMP scan patterns. */
function comparePosition(
  a: ComponentEntity,
  b: ComponentEntity,
  pattern: 'horizontal' | 'vertical',
  from: string,
  tolerance: number,
): number {
  const rightFirst = from.endsWith('right');
  const bottomFirst = from.startsWith('lower');

  const ax = rightFirst ? -a.x : a.x;
  const bx = rightFirst ? -b.x : b.x;
  // Document y increases upward, so an upper-left scan wants descending y.
  const ay = bottomFirst ? a.y : -a.y;
  const by = bottomFirst ? b.y : -b.y;

  if (pattern === 'horizontal') {
    // Band into rows first so a row reads left to right even when components
    // are not perfectly aligned.
    if (Math.abs(ay - by) > tolerance) return ay - by;
    return ax - bx;
  }
  if (Math.abs(ax - bx) > tolerance) return ax - bx;
  return ay - by;
}

/**
 * Strip the gate letter from a designator: `U1a` -> `U1`.
 * Two gates of one package share a physical part on the board.
 */
export function packageOf(ref: string): string {
  return ref.replace(/([A-Za-z_]+\d+)[a-z]$/, '$1');
}
